import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { getRowStyle } from "../utils/utils";
import { destinations } from "../constants/destinations";
import {ChevronDown} from "lucide-react"

import {
  getTransfer,
  historicalTransfer,
  syncTransfersToShopify,
  updateTransfer,
} from "../api/workOrder";
import toast, { Toaster } from "react-hot-toast";
import SingleDatePicker from "./SingleDatePicker";
import { CheckIcon, DocumentDuplicateIcon } from "@heroicons/react/24/outline";
import { useAuth } from "../context/AuthContext";
import ConfirmModal from "../modals/transferConfirmModal";

const SkeletonRow = () => (
  <tr className="animate-pulse">
    {Array.from({ length: 5 + 3 }).map((_, i) => (
      <td key={i} className="py-2 px-4 border-b">
        <div className="h-4 bg-gray-200 rounded"></div>
      </td>
    ))}
  </tr>
);

const Transfers = ({ isReadOnly, onRelease }) => {
  const navigate = useNavigate();
  const [selectedWorkOrders, setSelectedWorkOrders] = useState(new Set());
  const [trxColumns, setTrxColumns] = useState([
    { id: "TRX1", destination: "The Store" },
  ]);
  const [transferQuantities, setTransferQuantities] = useState({});
  const [filterColumn, setFilterColumn] = useState(null);
  const [warning, setWarning] = useState("");
  const [pendingWorkOrders, setPendingWorkOrders] = useState([]);
  const [historicalTransfers, setHistoricalTransfers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("pending");
  const [historicalFilter, setHistoricalFilter] = useState("");
  const [historicalSyncFilter, setHistoricalSyncFilter] = useState("all");
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [historicalWarehouseFilter, setHistoricalWarehouseFilter] =
    useState(null);
  const [copiedColumn, setCopiedColumn] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [syncSuccess, setSyncSuccess] = useState(null);
  const [useDateFilter, setUseDateFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
   const [isOpen , setIsOpen] = useState(false)

  const itemsPerPage = 25;
  const { canWrite } = useAuth();
  const canManageTransfers = canWrite("ERPagTransfers");

  const isVracItem = (title) => {
    return title?.toLowerCase().endsWith("vrac");
  };

  const clearSelectedWorkOrders = () => {
    setSelectedWorkOrders(new Set());
  };

  const formatDateKey = (date) =>
    date.toISOString().slice(0, 10).replace(/-/g, "");
  const formatDisplayDate = (date) =>
    date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });

  const dateKey = formatDateKey(selectedDate);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const handleDateChange = (date) => {
    setSelectedDate(date || new Date());
  };

  const handleDayNavigation = (direction) => {
    const newDate = new Date(selectedDate);
    newDate.setDate(selectedDate.getDate() + (direction === "prev" ? -1 : 1));
    setSelectedDate(newDate);
  };

  const handleHistoricalWarehouseFilterChange = (value) => {
    setHistoricalWarehouseFilter(
      value === "All Warehouses" || !value ? null : value,
    );
  };

  const fetchPendingTransfers = async () => {
    setIsLoading(true);
    try {
      const response = await getTransfer();
      const fetchedOrders = (response.data?.data || []).filter(
        (wo) => wo && wo.woNumber && wo.sku,
      );
      const mappedOrders = fetchedOrders.map((wo) => ({
        _id: wo._id,
        woNumber: wo.woNumber,
        scheduledTime: wo.scheduledTime || null,
        projectNumber: wo.projectNumber,
        sku: wo.sku,
        title: wo.title,
        labelCategory: wo.label || "N/A",
        allergen: wo.allergens === "0" ? "None" : wo.allergens || "None",
        netWeight: wo.netWeight || "N/A",
        plannedQty: parseFloat(wo.plannedQty) || 0,
        actualQty: parseFloat(wo.actualQty) || 0,
        lotNumber: wo.lotNumber || "N/A",
        warehouse:
          wo.status === "Approved"
            ? "QC HOLD"
            : wo.status === "Completed"
              ? "COMPLETED"
              : "LAB HOLD",
        status: wo.status,
        isArchived: false,
      }));
      setPendingWorkOrders(mappedOrders);
      setSelectedWorkOrders(new Set(mappedOrders.map((wo) => wo.woNumber)));
      setWarning("");
    } catch (err) {
      setWarning(err.message || "Failed to fetch pending transfers.");
      setPendingWorkOrders([]);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchHistoricalTransfers = async () => {
    setIsLoading(true);
    try {
      let response;
      const syncParam =
        historicalSyncFilter === "all" ? undefined : historicalSyncFilter;
      if (useDateFilter && selectedDate) {
        const formattedDate = selectedDate.toISOString().slice(0, 10);
        response = await historicalTransfer(formattedDate, syncParam);
      } else {
        response = await historicalTransfer(undefined, syncParam);
      }

      const fetchedTransfers = (response.data?.data || []).filter(
        (record) => record && record.woNumber,
      );

      const mappedTransfers = fetchedTransfers.map((record) => {
        const transfersArray = record.transfer || [];
        const combinedDestinations =
          transfersArray.length > 0
            ? transfersArray
                .map(
                  (t) =>
                    `${t.destination || "Unknown"} (${parseFloat(t.transferQty) || 0})`,
                )
                .join("\n")
            : "No transfers";

        const destinationsList =
          transfersArray.length > 0
            ? [
                ...new Set(
                  transfersArray
                    .map((t) => {
                      const d = (t.destination || "").trim();
                      return d.replace(/\s*\([\d.,]+\)\s*$/, "").trim();
                    })
                    .filter((d) => d && d !== "Unknown"),
                ),
              ]
            : typeof record.destination === "string"
              ? [
                  ...new Set(
                    record.destination
                      .split("\n")
                      .map((d) => d.replace(/\s*\([\d.,]+\)\s*$/, "").trim())
                      .filter(
                        (d) => d && d !== "Unknown" && d !== "No transfers",
                      ),
                  ),
                ]
              : [];

        return {
          scheduledTime: record.scheduledTime || null,
          woNumber: record.woNumber,
          projectNumber: record.projectNumber,
          sku: record.sku,
          title: record.title,
          labelCategory: record.label || "N/A",
          allergen: record.allergens || "None",
          netWeight: record.netWeight || "N/A",
          plannedQty: parseFloat(record.plannedQty) || 0,
          actualQty: parseFloat(record.actualQty) || 0,
          lotNumber: record.lotNumber || "N/A",
          warehouse:
            record.status === "Approved"
              ? "QC HOLD"
              : record.status === "Completed"
                ? "COMPLETED"
                : "LAB HOLD",
          status: record.status || "Unknown",
          destination: combinedDestinations,
          destinationsList,
          date: record.createdAt
            ? new Date(record.createdAt).toISOString().slice(0, 10)
            : "Unknown",
          createdAt: record.createdAt, // keep raw for potential future sort
          syncStatus: record.syncStatus || "not_synced",
        };
      });

      setHistoricalTransfers(mappedTransfers);
      setWarning("");
    } catch (err) {
      setWarning(err.message || "Failed to fetch historical transfers.");
      setHistoricalTransfers([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingTransfers();
  }, []);

  useEffect(() => {
    if (activeTab === "historical") {
      fetchHistoricalTransfers();
    }
  }, [activeTab, useDateFilter, selectedDate, historicalSyncFilter]);

  useEffect(() => {
    const initialQuantities = {};
    pendingWorkOrders.forEach((wo) => {
      const remainingQty = Math.max(0, wo.actualQty || 0);
      initialQuantities[wo.woNumber] = {};
      trxColumns.forEach((col, index) => {
        initialQuantities[wo.woNumber][col.id] =
          index === 0 ? remainingQty : 0;
      });
    });

    if (
      JSON.stringify(initialQuantities) !== JSON.stringify(transferQuantities)
    ) {
      setTransferQuantities(initialQuantities);
    }
  }, [pendingWorkOrders, trxColumns]);

  useEffect(() => {
    if (copiedColumn) {
      const timer = setTimeout(() => {
        setCopiedColumn(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedColumn]);

  const filteredTransferRows = useMemo(() => {
    const nonArchivedWorkOrders = pendingWorkOrders.filter(
      (wo) => !wo.isArchived,
    );
    if (!filterColumn) return nonArchivedWorkOrders;
    return nonArchivedWorkOrders.filter(
      (wo) => (transferQuantities[wo.woNumber]?.[filterColumn] || 0) > 0,
    );
  }, [pendingWorkOrders, transferQuantities, filterColumn]);

  useEffect(() => {
    filteredTransferRows.forEach((wo) => {
      console.log(
        `WO: ${wo.woNumber}, allergen: ${wo.allergen}, warehouse: ${wo.warehouse}, style:`,
        getRowStyle(wo.allergen, wo.warehouse, null),
      );
    }); // check git
  }, [filteredTransferRows]);

  const filteredHistoricalTransfers = useMemo(() => {
    let filtered = historicalTransfers;
    if (historicalFilter) {
      const lowerFilter = historicalFilter.toLowerCase();
      filtered = filtered.filter((record) =>
        [
          record.transferId,
          record.woNumber,
          record.projectNumber,
          record.sku,
          record.title,
          record.labelCategory,
          record.allergen,
          record.lotNumber,
          record.warehouse,
          record.destination,
          record.syncStatus,
          record.date,
        ].some((field) => field?.toLowerCase().includes(lowerFilter)),
      );
    }
    if (historicalWarehouseFilter) {
      const filterLower = historicalWarehouseFilter.toLowerCase().trim();
      filtered = filtered.filter((record) => {
        if (
          Array.isArray(record.destinationsList) &&
          record.destinationsList.length > 0
        ) {
          return record.destinationsList.some(
            (d) => d.toLowerCase().trim() === filterLower,
          );
        }
        return record.destination?.toLowerCase().includes(filterLower);
      });
    }
    if (historicalSyncFilter !== "all") {
      filtered = filtered.filter(
        (record) => record.syncStatus === historicalSyncFilter,
      );
    }
    return filtered;
  }, [
    historicalTransfers,
    historicalFilter,
    historicalWarehouseFilter,
    historicalSyncFilter,
  ]);

  const totalPages = Math.ceil(
    filteredHistoricalTransfers.length / itemsPerPage,
  );
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    const end = start + itemsPerPage;
    return filteredHistoricalTransfers.slice(start, end);
  }, [filteredHistoricalTransfers, currentPage]);

  const uniqueDestinations = useMemo(() => {
    const destinationsSet = new Set();
    historicalTransfers.forEach((record) => {
      if (
        Array.isArray(record.destinationsList) &&
        record.destinationsList.length > 0
      ) {
        record.destinationsList.forEach((dest) => {
          if (dest && dest.trim() && dest.trim() !== "Unknown") {
            destinationsSet.add(dest.trim());
          }
        });
      } else if (record.destination && record.destination !== "No transfers") {
        record.destination.split("\n").forEach((line) => {
          const clean = line.replace(/\s*\([\d.,]+\)\s*$/, "").trim();
          if (clean && clean !== "Unknown" && clean !== "No transfers") {
            destinationsSet.add(clean);
          }
        });
      }
    });
    return Array.from(destinationsSet).sort((a, b) => a.localeCompare(b));
  }, [historicalTransfers]);

  const handleTransferQuantityChange = (woNumber, trxId, value) => {
    const wo = pendingWorkOrders.find((w) => w.woNumber === woNumber);
    if (!wo) return;

    const isVrac = isVracItem(wo.title);
    const parsedValue = isVrac ? parseFloat(value) || 0 : parseInt(value) || 0;
    const newValue = Math.max(0, parsedValue);

    const otherTrxTotal = Object.entries(transferQuantities[woNumber] || {})
      .filter(([key]) => key !== trxId)
      .reduce((sum, [, qty]) => sum + (qty || 0), 0);

    const maxAllowed = Math.max(
      0,
      (wo.actualQty || 0) - otherTrxTotal,
    );

    if (newValue > maxAllowed) {
      toast.error(
        `Cannot transfer more than ${maxAllowed}.`,
        {
          id: `quantity-error-${woNumber}-${trxId}`,
          duration: 3000,
          position: "top-right",
        },
      );
      return;
    }

    const finalValue = newValue;

    setTransferQuantities((prev) => ({
      ...prev,
      [woNumber]: {
        ...prev[woNumber],
        [trxId]: finalValue,
      },
    }));
  };

  const handleWorkOrderSelectionChange = (woNumber, checked) => {
    setSelectedWorkOrders((prev) => {
      const updated = new Set(prev);
      if (checked) {
        updated.add(woNumber);
      } else {
        updated.delete(woNumber);
      }
      return updated;
    });
  };

  const handleSelectAllVisible = (checked) => {
    setSelectedWorkOrders((prev) => {
      const updated = new Set(prev);
      filteredTransferRows.forEach((wo) => {
        if (checked) {
          updated.add(wo.woNumber);
        } else {
          updated.delete(wo.woNumber);
        }
      });
      return updated;
    });
  };

  const buildTransferDetailsForWorkOrder = (woNumber) => {
    const quantities = transferQuantities[woNumber] || {};

    return Object.entries(quantities)
      .filter(([, qty]) => Number(qty) > 0)
      .map(([trxId, qty]) => {
        const column = trxColumns.find((col) => col.id === trxId);
        if (!column?.destination || column.destination.trim() === "") {
          throw new Error(`Invalid destination for ${trxId}`);
        }
        return {
          destination: column.destination,
          transferQty: Number(qty),
        };
      });
  };

  const getSelectedTransfers = () => {
    return pendingWorkOrders
      .filter((wo) => selectedWorkOrders.has(wo.woNumber))
      .map((wo) => {
        const transferDetails = buildTransferDetailsForWorkOrder(wo.woNumber);
        if (transferDetails.length === 0) return null;
        return {
          workOrderId: wo._id,
          transferDetails,
        };
      })
      .filter(Boolean);
  };

  const handleSyncToShopify = async () => {
    if (isReadOnly || !canManageTransfers) {
      toast.error("No permission to sync to Shopify.");
      return;
    }

    let transfers = [];
    try {
      transfers = getSelectedTransfers().map((t) => ({
        ...t,
        transferDetails: t.transferDetails.map((d) => ({
          ...d,
          destination:
            d.destination === "The Store" ? "Warehouse 1580" : d.destination,
        })),
      }));
    } catch (error) {
      toast.error(error.message || "Invalid destination detected.");
      return;
    }

    if (transfers.length === 0) {
      toast.error("Select at least one row with valid transfer quantities.");
      return;
    }

    const shopifyRelevantTransfers = transfers.filter((t) =>
      t.transferDetails.some((d) => d.destination === "Warehouse 1580"),
    );

    if (shopifyRelevantTransfers.length === 0) {
      toast(
        "No transfers destined for 'The Store' (Warehouse 1580). Nothing to sync.",
        {
          icon: "ℹ️",
          duration: 4000,
        },
      );
      return;
    }

    setIsSyncing(true);
    setSyncError(null);
    setSyncSuccess(null);

    try {
      const result = await syncTransfersToShopify(shopifyRelevantTransfers);

      toast.success(
        `Successfully synced ${result.synced?.length || 0} items to Shopify Warehouse 1580!`,
        {
          duration: 5000,
        },
      );

      if (result.failed?.length > 0) {
        toast(
          `Warning: ${result.failed.length} items failed to sync – check details in console.`,
          {
            icon: "⚠️",
            duration: 6000,
          },
        );
      }

      setSyncSuccess(result);

      // Refresh UI
      await fetchPendingTransfers();
      await fetchHistoricalTransfers();
    } catch (err) {
      setSyncError(err);
      toast.error(`Sync failed: ${err}`);
    } finally {
      setIsSyncing(false);
    }
  };

  const handleClearColumnQuantities = (trxId) => {
    setTransferQuantities((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((woNumber) => {
        updated[woNumber] = { ...updated[woNumber], [trxId]: 0 };
      });
      return updated;
    });
  };

  const areColumnQuantitiesZero = (trxId) => {
    return Object.values(transferQuantities).every(
      (quantities) => (quantities[trxId] || 0) === 0,
    );
  };

  const handleCopyColumn = async (column) => {
    let dataToCopy = "";

    if (column === "Remaining Balance in Factory") {
      dataToCopy = filteredTransferRows
        .map((wo) =>
          String(
            Math.max(
              0,
              (wo.actualQty || 0) -
                Object.values(transferQuantities[wo.woNumber] || {}).reduce(
                  (sum, qty) => sum + (qty || 0),
                  0,
                ),
            ),
          ),
        )
        .join("\r\n");
    } else if (["SKU", "Title", "Actual Qty", "Lot Number"].includes(column)) {
      const keyMap = {
        SKU: "sku",
        Title: "title",
        "Actual Qty": "actualQty",
        "Lot Number": "lotNumber",
      };
      dataToCopy = filteredTransferRows
        .map((wo) =>
          String(wo[keyMap[column]] || (column === "Actual Qty" ? 0 : "N/A")),
        )
        .join("\r\n");
    } else {
      // Dynamic TRX columns (TRX1, TRX2, etc.)
      dataToCopy = filteredTransferRows
        .map((wo) => String(transferQuantities[wo.woNumber]?.[column] || 0))
        .join("\r\n");
    }

    try {
      await navigator.clipboard.writeText(dataToCopy);
      setCopiedColumn(column);
      toast.success("Copied!", {
        id: `copy-success-${column}`,
        duration: 2000,
        position: "top-right",
      });
    } catch (err) {
      toast.error("Failed to copy column!", {
        id: `copy-error-${column}`,
        duration: 2000,
        position: "top-right",
      });
    }
  };

  const addTrxColumn = () => {
    if (trxColumns.length < 3) {
      const newId = `TRX${trxColumns.length + 1}`;
      setTrxColumns((prev) => [...prev, { id: newId, destination: "Factory" }]);
      setTransferQuantities((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((woNumber) => {
          updated[woNumber] = { ...updated[woNumber], [newId]: 0 };
        });
        return updated;
      });
    }
  };

  const removeTrxColumn = (trxId) => {
    setTrxColumns((prev) => prev.filter((col) => col.id !== trxId));
    setTransferQuantities((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((woNumber) => {
        delete updated[woNumber][trxId];
      });
      return updated;
    });
    if (filterColumn === trxId) setFilterColumn(null);
  };

  const handleDestinationChange = (trxId, value) => {
    let newDestination = value;
    if (value === "Online") {
      newDestination = "Online";
    } else if (value === "Custom") {
      newDestination = "";
    }
    setTrxColumns((prev) =>
      prev.map((col) =>
        col.id === trxId ? { ...col, destination: newDestination } : col,
      ),
    );
  };

  const handleCustomDestinationChange = (trxId, value) => {
    setTrxColumns((prev) =>
      prev.map((col) =>
        col.id === trxId ? { ...col, destination: value } : col,
      ),
    );
  };

  const confirmTransfer = async () => {
    if (isReadOnly) {
      toast.error("Cannot confirm transfers in read-only mode.", {
        id: "confirm-readonly-error",
        duration: 2000,
        position: "top-right",
      });
      return;
    }

    // Build transfers payload from selected work orders only
    let transfers = [];
    try {
      transfers = getSelectedTransfers();
    } catch (error) {
      toast.error(error.message || "Invalid destination detected.", {
        id: "confirm-invalid-destination-error",
        duration: 2500,
        position: "top-right",
      });
      return;
    }

    if (transfers.length === 0) {
      toast.error("Select at least one work order with transfer quantities.", {
        id: "confirm-no-transfers-error",
        duration: 2000,
        position: "top-right",
      });
      return;
    }

    // Filter Shopify-relevant transfers
    const shopifyRelevantTransfers = transfers.filter((t) =>
      t.transferDetails.some((d) => d.destination === "The Store"),
    );

    setIsLoading(true);

    // Step 1: Sync to Shopify if there are relevant transfers
    let syncSuccess = true;
    let syncMessage = "";

    if (shopifyRelevantTransfers.length > 0 && canManageTransfers) {
      try {
        const result = await syncTransfersToShopify(shopifyRelevantTransfers);
        const syncedWorkOrderIds = new Set(
          (result.synced || []).map((row) => String(row.workOrderId)),
        );
        transfers = transfers.map((t) => {
          const hasShopifyDestination = t.transferDetails.some(
            (d) => d.destination === "The Store",
          );
          if (!hasShopifyDestination) {
            return { ...t, syncStatus: "not_synced" };
          }
          return {
            ...t,
            syncStatus: syncedWorkOrderIds.has(String(t.workOrderId))
              ? "synced"
              : "not_synced",
          };
        });
        syncMessage = `Shopify: ${result.synced?.length || 0} items synced successfully.`;
        if (result.failed?.length > 0) {
          syncMessage += ` (${result.failed.length} failed)`;
        }
        toast.success(syncMessage, { duration: 5000, position: "top-right" });
      } catch (syncErr) {
        syncSuccess = false;
        toast.error(
          `Shopify sync failed: ${syncErr.message || "Unknown error"}`,
          {
            duration: 6000,
          },
        );
      }
    }
    if (shopifyRelevantTransfers.length === 0 || !canManageTransfers) {
      transfers = transfers.map((t) => ({ ...t, syncStatus: "not_synced" }));
    }

    // Step 2: STOP if sync failed (Option B)
    if (!syncSuccess) {
      setIsLoading(false);
      return; // Do NOT proceed to historical update
    }

    // Step 3: Proceed to historical update
    const payload = { transfers };

    try {
      await updateTransfer(payload);

      toast.success("Transfers recorded and moved to historical!", {
        id: "confirm-success",
        duration: 3000,
        position: "top-right",
      });

      // Reset UI
      setSelectedWorkOrders(new Set());
      setTransferQuantities({});
      setTrxColumns([{ id: "TRX1", destination: "The Store" }]);
      setFilterColumn(null);
      setWarning("");

      await fetchPendingTransfers();
      await fetchHistoricalTransfers();
    } catch (error) {
      toast.error(error.message || "Failed to confirm transfers.", {
        id: "confirm-error",
        duration: 2000,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    clearSelectedWorkOrders();
    setTransferQuantities({});
    setTrxColumns([{ id: "TRX1", destination: "The Store" }]);
    setFilterColumn(null);
    setWarning("");
    navigate("/on-hold");
  };

  const handleMoveToHistoricalWithoutSync = async (wo) => {
    if (isReadOnly || !canManageTransfers) {
      toast.error("No permission to move this row.");
      return;
    }

    let transferDetails = [];
    try {
      transferDetails = buildTransferDetailsForWorkOrder(wo.woNumber);
    } catch (error) {
      toast.error(error.message || "Invalid transfer details.");
      return;
    }

    if (transferDetails.length === 0) {
      toast.error(
        "Enter transfer quantity for this work order before moving to historical.",
      );
      return;
    }

    setIsLoading(true);
    try {
      await updateTransfer({
        transfers: [
          {
            workOrderId: wo._id,
            transferDetails,
            syncStatus: "not_synced",
          },
        ],
      });

      toast.success(`${wo.woNumber} moved to historical (without sync).`, {
        duration: 3000,
      });

      setSelectedWorkOrders((prev) => {
        const updated = new Set(prev);
        updated.delete(wo.woNumber);
        return updated;
      });

      await fetchPendingTransfers();
      await fetchHistoricalTransfers();
    } catch (error) {
      toast.error(error.message || "Failed to move work order to historical.");
    } finally {
      setIsLoading(false);
    }
  };

  const hasPendingData = pendingWorkOrders.length > 0;
  const hasFilterMatch = filteredTransferRows.length > 0;
  const isFiltered = filterColumn !== null;
  const selectedVisibleCount = filteredTransferRows.filter((wo) =>
    selectedWorkOrders.has(wo.woNumber),
  ).length;
  const allVisibleSelected =
    filteredTransferRows.length > 0 &&
    selectedVisibleCount === filteredTransferRows.length;

  const hasUnsavedChanges = useMemo(() => {
    return Object.values(transferQuantities).some((quantities) =>
      Object.values(quantities).some((qty) => Number(qty) > 0),
    );
  }, [transferQuantities]);

  return (
    <div className="bg-white p-4 sm:p-6 rounded-lg shadow-lg border border-gray-200">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg sm:text-xl font-semibold pb-3 border-b border-gray-200 text-gray-800">
          Transfers
        </h3>
      </div>
      <Toaster />
      {warning && (
        <div className="mb-4 p-3 bg-yellow-100 text-yellow-800 text-sm rounded border border-yellow-300">
          {warning}
        </div>
      )}

      <div className="flex border-b border-gray-200 mb-4 gap-2">
        <button
          className={`relative flex items-center gap-2 py-2 px-5 text-sm font-semibold transition-colors ${
            activeTab === "pending"
              ? "text-[#365daf]"
              : "text-gray-600 hover:text-gray-800"
          }`}
          onClick={() => setActiveTab("pending")}
        >
          <span>Pending Transfers</span>

          {hasUnsavedChanges && (
            <span className="text-[10px] font-semibold px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full border border-amber-300 uppercase tracking-wide">
              Unsaved
            </span>
          )}

          {activeTab === "pending" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#365daf] rounded-t"></span>
          )}
        </button>
        <button
          className={`relative py-2 px-5 text-sm font-semibold transition-colors ${
            activeTab === "historical"
              ? "text-[#365daf]"
              : "text-gray-600 hover:text-gray-800"
          }`}
          onClick={() => setActiveTab("historical")}
          aria-label="View Historical Transfers"
        >
          Historical Transfers
          {activeTab === "historical" && (
            <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#365daf] rounded-t"></span>
          )}
        </button>
      </div>

      {activeTab === "pending" && (
        <div>
          <div className="mb-4">
            {hasUnsavedChanges && (
              <div className="mt-2 flex items-start gap-2 rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
                <span className="text-amber-500 mt-[2px]">⚠️</span>
                <p className="text-s text-amber-800 leading-snug">
                  Please note that changes will be reset if you leave this tab
                  or refresh the page.
                </p>
              </div>
            )}
          </div>

          {isLoading ? (
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <tbody>
                  {Array.from({ length: 5 }).map((_, i) => (
                    <SkeletonRow key={i} />
                  ))}
                </tbody>
              </table>
            </div>
          ) : !hasPendingData ? (
            <p className="text-sm text-gray-600 mb-4">
              No pending transfers available.
            </p>
          ) : (
            <>
              {/* Always visible: Filter Controls */}
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 mb-4 sm:mb-6">
                <button
                  onClick={addTrxColumn}
                  disabled={
                    trxColumns.length >= 3 || isReadOnly || !canManageTransfers
                  }
                  className="py-1 sm:py-2 px-3 sm:px-4 rounded bg-[#365daf] hover:bg-blue-900 text-white text-xs sm:text-sm font-semibold disabled:bg-gray-300 disabled:cursor-not-allowed"
                  title={
                    !canManageTransfers
                      ? "You do not have permission to add destinations"
                      : ""
                  }
                >
                  + Add Destination
                </button>

                <div className="flex items-center gap-2">
                  <select
                    value={filterColumn || ""}
                    onChange={(e) => setFilterColumn(e.target.value || null)}
                    className="py-1 sm:py-2 px-2 rounded border border-gray-300 text-xs sm:text-sm text-gray-900"
                    aria-label="Filter by TRX column"
                  >
                    <option value="">Show All</option>
                    {trxColumns.map((col) => (
                      <option key={col.id} value={col.id}>
                        {col.id} ({col.destination})
                      </option>
                    ))}
                  </select>

                  {isFiltered && (
                    <button
                      onClick={() => setFilterColumn(null)}
                      className="text-xs text-red-600 hover:text-red-800 underline"
                    >
                      Clear Filter
                    </button>
                  )}
                </div>
              </div>

              {/* Show warning if filtered and no matches */}
              {!hasFilterMatch && isFiltered && (
                <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 font-medium">
                    No work orders have quantity in{" "}
                    <strong>{filterColumn}</strong>.
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    Try adjusting quantities or select "Show All" above.
                  </p>
                </div>
              )}

              {/* Show table only if there's data (or not filtered) */}
              {(hasFilterMatch || !isFiltered) && (
                <>
                  {/* Desktop Table */}
                  <div className="hidden sm:block overflow-x-auto mb-4 sm:mb-6">
                    <table className="w-full border-collapse">
                      <thead>
                        <tr>
                          <th className="py-1 sm:py-2 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300">
                            <div className="flex items-center gap-2">
                              <input
                                type="checkbox"
                                checked={allVisibleSelected}
                                onChange={(e) =>
                                  handleSelectAllVisible(e.target.checked)
                                }
                                className="h-4 w-4 rounded border-gray-300"
                                aria-label="Select all visible work orders"
                              />
                            </div>
                          </th>
                          {[
                            "SKU",
                            "Title",
                            "Actual Qty",
                            "Lot Number",
                            "Remaining Balance in Factory",
                          ].map((header) => (
                            <th
                              key={header}
                              className="py-1 sm:py-2 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300"
                            >
                              <div className="flex items-start gap-2">
                                <span>{header}</span>

                                <button
                                  onClick={() => handleCopyColumn(header)}
                                  disabled={filteredTransferRows.length === 0}
                                  className="text-[#d73094] disabled:text-gray-400 disabled:cursor-not-allowed mt-[2px]"
                                  title={`Copy ${header} column`}
                                >
                                  {copiedColumn === header ? (
                                    <CheckIcon className="h-5 w-5" />
                                  ) : (
                                    <DocumentDuplicateIcon className="h-5 w-5" />
                                  )}
                                </button>
                              </div>
                            </th>
                          ))}
                          {trxColumns.map((col) => (
                            <th
                              key={col.id}
                              className="py-1 sm:py-2 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300"
                            >
                              <div className="flex flex-col gap-1">
                                <span>
                                  {col.id} ({col.destination})
                                </span>
                                <select
                                  value={
                                    col.destination === ""
                                      ? "Custom"
                                      : col.destination
                                  }
                                  onChange={(e) =>
                                    handleDestinationChange(
                                      col.id,
                                      e.target.value,
                                    )
                                  }
                                  className="py-1 px-2 rounded border border-gray-300 text-xs text-gray-900"
                                  aria-label={`Select destination for ${col.id}`}
                                >
                                  {destinations.map((dest) => (
                                    <option key={dest} value={dest}>
                                      {dest}
                                    </option>
                                  ))}
                                  <option value="Custom">Custom</option>
                                </select>
                                {col.destination === "" && (
                                  <input
                                    type="text"
                                    placeholder="Custom destination"
                                    value={col.destination}
                                    onChange={(e) =>
                                      handleCustomDestinationChange(
                                        col.id,
                                        e.target.value,
                                      )
                                    }
                                    className="py-1 px-2 rounded border border-gray-300 text-xs text-gray-900"
                                    aria-label={`Custom destination for ${col.id}`}
                                  />
                                )}
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => handleCopyColumn(col.id)}
                                    disabled={filteredTransferRows.length === 0}
                                    className="text-[#d73094] disabled:text-gray-400 disabled:cursor-not-allowed"
                                    title={`Copy ${col.id} column`}
                                    aria-label={`Copy ${col.id} column`}
                                  >
                                    {copiedColumn === col.id ? (
                                      <CheckIcon className="h-5 w-5" />
                                    ) : (
                                      <DocumentDuplicateIcon className="h-5 w-5" />
                                    )}
                                  </button>
                                  {col.id !== "TRX1" && (
                                    <>
                                      <button
                                        onClick={() =>
                                          handleClearColumnQuantities(col.id)
                                        }
                                        disabled={
                                          isReadOnly ||
                                          areColumnQuantitiesZero(col.id)
                                        }
                                        className="text-blue-600 hover:text-blue-800 text-xs disabled:text-gray-400 disabled:cursor-not-added"
                                        aria-label={`Clear quantities for ${col.id}`}
                                      >
                                        Clear
                                      </button>
                                      <button
                                        onClick={() => removeTrxColumn(col.id)}
                                        disabled={!canManageTransfers}
                                        className="text-red-600 hover:text-red-800 text-xs disabled:text-gray-400 disabled:cursor-not-allowed"
                                        title={
                                          !canManageTransfers
                                            ? "You do not have permission to remove columns"
                                            : "Remove column"
                                        }
                                        aria-label={`Remove ${col.id} column`}
                                      >
                                        Remove
                                      </button>
                                    </>
                                  )}
                                </div>
                              </div>
                            </th>
                          ))}
                          <th className="py-1 sm:py-2 px-2 sm:px-4 text-left bg-gray-200 text-gray-900 font-semibold uppercase text-xs border-b border-gray-300">
                            Actions
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredTransferRows.map((wo) => (
                          <tr
                            key={wo.woNumber}
                            className="border-b border-gray-200"
                            style={getRowStyle(wo.allergen, wo.warehouse, null)}
                          >
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">
                              <input
                                type="checkbox"
                                checked={selectedWorkOrders.has(wo.woNumber)}
                                onChange={(e) =>
                                  handleWorkOrderSelectionChange(
                                    wo.woNumber,
                                    e.target.checked,
                                  )
                                }
                                className="h-4 w-4 rounded border-gray-300"
                                aria-label={`Select ${wo.woNumber}`}
                                disabled={isReadOnly || !canManageTransfers}
                              />
                            </td>
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 select-text">
                              {wo.sku || "N/A"}
                            </td>
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 select-text">
                              {wo.title}
                            </td>
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 select-text">
                              {wo.actualQty || 0}
                            </td>
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 select-text">
                              {wo.lotNumber || "N/A"}
                            </td>
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900 select-text">
                              {Math.max(
                                0,
                                (wo.actualQty || 0) -
                                  Object.values(
                                    transferQuantities[wo.woNumber] || {},
                                  ).reduce((sum, qty) => sum + (qty || 0), 0),
                              ).toFixed(isVracItem(wo.title) ? 2 : 0)}
                            </td>
                            {trxColumns.map((col) => (
                              <td
                                key={col.id}
                                className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900"
                              >
                                <input
                                  type="number"
                                  min="0"
                                  step={isVracItem(wo.title) ? "0.1" : "1"}
                                  value={
                                    transferQuantities[wo.woNumber]?.[col.id] ||
                                    0
                                  }
                                  onChange={(e) =>
                                    handleTransferQuantityChange(
                                      wo.woNumber,
                                      col.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-24 sm:w-32 py-1 px-2 rounded border border-gray-300 text-xs sm:text-sm text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
                                  aria-label={`Transfer quantity for ${wo.woNumber} to ${col.destination}`}
                                  disabled={isReadOnly || !canManageTransfers}
                                />
                              </td>
                            ))}
                            <td className="py-1 sm:py-2 px-2 sm:px-4 text-xs sm:text-sm text-gray-900">
                              <button
                                type="button"
                                onClick={() =>
                                  handleMoveToHistoricalWithoutSync(wo)
                                }
                                disabled={
                                  isLoading || isReadOnly || !canManageTransfers
                                }
                                className="py-2 px-3 rounded bg-[#cf350d] text-white hover:bg-red-900  text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                Archive (No Sync)
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile View */}
                  <div className="block sm:hidden mb-4 space-y-4">
                    <div className="flex gap-2 mb-4">
                      <select
                        onChange={(e) =>
                          e.target.value && handleCopyColumn(e.target.value)
                        }
                        className="py-1 px-2 rounded border border-gray-300 text-xs text-gray-900"
                        aria-label="Select column to copy"
                      >
                        <option value="">Select Column to Copy</option>
                        {[
                          "SKU",
                          "Title",
                          "Actual Qty",
                          "Lot Number",
                          "Remaining Balance in Factory",
                        ].map((header) => (
                          <option key={header} value={header}>
                            <div className="flex items-center gap-2">
                              {copiedColumn === header ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <DocumentDuplicateIcon className="h-4 w-4 text-green-600" />
                              )}
                              {header}
                            </div>
                          </option>
                        ))}
                        {trxColumns.map((col) => (
                          <option key={col.id} value={col.id}>
                            <div className="flex items-center gap-2">
                              {copiedColumn === col.id ? (
                                <CheckIcon className="h-4 w-4 text-green-600" />
                              ) : (
                                <DocumentDuplicateIcon className="h-4 w-4 text-green-600" />
                              )}
                              {col.id} ({col.destination})
                            </div>
                          </option>
                        ))}
                      </select>
                    </div>

                    {filteredTransferRows.map((wo) => (
                      <div
                        key={wo.woNumber}
                        className="bg-gray-50 p-4 rounded-lg shadow-sm border border-gray-200"
                        style={getRowStyle(wo.allergen, wo.warehouse, null)}
                      >
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div className="font-semibold text-gray-700">
                            Select
                          </div>
                          <div>
                            <input
                              type="checkbox"
                              checked={selectedWorkOrders.has(wo.woNumber)}
                              onChange={(e) =>
                                handleWorkOrderSelectionChange(
                                  wo.woNumber,
                                  e.target.checked,
                                )
                              }
                              className="h-4 w-4 rounded border-gray-300"
                              aria-label={`Select ${wo.woNumber}`}
                              disabled={isReadOnly || !canManageTransfers}
                            />
                          </div>
                          <div className="font-semibold text-gray-700">SKU</div>
                          <div className="select-text">{wo.sku || "N/A"}</div>
                          <div className="font-semibold text-gray-700">
                            Title
                          </div>
                          <div className="select-text">{wo.title}</div>
                          <div className="font-semibold text-gray-700">
                            Actual Qty
                          </div>
                          <div className="select-text">{wo.actualQty || 0}</div>
                          <div className="font-semibold text-gray-700">
                            Lot Number
                          </div>
                          <div className="select-text">
                            {wo.lotNumber || "N/A"}
                          </div>
                          <div className="font-semibold text-gray-700">
                            Remaining Balance in Factory
                          </div>
                          <div className="select-text">
                            {Math.max(
                              0,
                              (wo.actualQty || 0) -
                                Object.values(
                                  transferQuantities[wo.woNumber] || {},
                                ).reduce((sum, qty) => sum + (qty || 0), 0),
                            ).toFixed(isVracItem(wo.title) ? 2 : 0)}
                          </div>
                          {trxColumns.map((col) => (
                            <React.Fragment key={col.id}>
                              <div className="font-semibold text-gray-700">
                                {col.id} ({col.destination})
                              </div>
                              <div>
                                <input
                                  type="number"
                                  min="0"
                                  step={isVracItem(wo.title) ? "0.1" : "1"}
                                  value={
                                    transferQuantities[wo.woNumber]?.[col.id] ||
                                    0
                                  }
                                  onChange={(e) =>
                                    handleTransferQuantityChange(
                                      wo.woNumber,
                                      col.id,
                                      e.target.value,
                                    )
                                  }
                                  className="w-full py-1 px-2 rounded border border-gray-300 text-xs text-gray-900 focus:ring-2 focus:ring-gray-500 focus:outline-none"
                                  aria-label={`Transfer quantity for ${wo.woNumber} to ${col.destination}`}
                                  disabled={isReadOnly || !canManageTransfers}
                                />
                                {col.id !== "TRX1" && (
                                  <div className="flex gap-2 mt-1">
                                    <button
                                      onClick={() =>
                                        handleClearColumnQuantities(col.id)
                                      }
                                      disabled={
                                        isReadOnly ||
                                        areColumnQuantitiesZero(col.id)
                                      }
                                      className="text-blue-600 hover:text-blue-800 text-xs disabled:text-gray-400 disabled:cursor-not-allowed"
                                      aria-label={`Clear quantities for ${col.id}`}
                                    >
                                      Clear
                                    </button>
                                    <button
                                      onClick={() => removeTrxColumn(col.id)}
                                      disabled={!canManageTransfers}
                                      className="text-red-600 hover:text-red-800 text-xs disabled:text-gray-400 disabled:cursor-not-allowed"
                                      title={
                                        !canManageTransfers
                                          ? "You do not have permission to remove columns"
                                          : "Remove column"
                                      }
                                      aria-label={`Remove ${col.id} column`}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                )}
                              </div>
                            </React.Fragment>
                          ))}
                          <div className="font-semibold text-gray-700">
                            Actions
                          </div>
                          <div>
                            <button
                              type="button"
                              onClick={() =>
                                handleMoveToHistoricalWithoutSync(wo)
                              }
                              disabled={
                                isLoading || isReadOnly || !canManageTransfers
                              }
                              className="py-1 px-2 rounded bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              Move to Historical
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 mt-6">
                    {/* <button
                      onClick={handleCancel}
                      className="py-1 sm:py-2 px-3 sm:px-4 rounded bg-gray-200 hover:bg-gray-300 text-gray-900 text-xs sm:text-sm font-semibold"
                      title="  This will discard all current changes and return to the
                        On-Hold page."
                    >
                      Cancel
                    </button> */}
                    <div className="flex items-center text-xs sm:text-sm text-gray-700">
                      Selected: {selectedWorkOrders.size}
                    </div>
                    <button
                      onClick={() => setShowConfirmModal(true)}
                      disabled={isLoading || selectedWorkOrders.size === 0}
                      className={`py-1 sm:py-2 px-5 sm:px-6 rounded text-xs sm:text-sm font-semibold transition shadow-sm
    ${isLoading ? "bg-gray-400 text-gray-600 cursor-wait" : "bg-black hover:bg-gray-700 text-white"}
    disabled:opacity-50 disabled:cursor-not-allowed
  `}
                    >
                      {isLoading
                        ? "Processing..."
                        : "Sync Shopify & Move to Historical"}
                    </button>
                  </div>
                  {syncError && (
                    <div className="mt-4 p-3 bg-red-100 text-red-800 rounded border border-red-300 text-sm">
                      Sync failed: {syncError}
                    </div>
                  )}

                  {syncSuccess && (
                    <div className="mt-4 p-3 bg-green-100 text-green-800 rounded border border-green-300 text-sm">
                      Success! Synced {syncSuccess.synced?.length || 0} items.
                      Failed: {syncSuccess.failed?.length || 0}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      )}

      {activeTab === "historical" && (
        <div className="space-y-5">
          {/* Header + Stats */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h4 className="text-lg font-semibold text-gray-900">
              Historical Transfers
              {historicalTransfers.length > 0 && (
                <span className="ml-3 text-sm font-normal text-gray-500">
                  ({historicalTransfers.length} workOrder
                  {historicalTransfers.length !== 1 ? "s" : ""})
                </span>
              )}
            </h4>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 shadow-sm">
            <div className="flex flex-col sm:flex-row sm:items-end sm:flex-wrap gap-3 sm:gap-4">
              {/* Search */}
              <div className="flex-1 min-w-[240px]">
                <input
                  type="search"
                  value={historicalFilter}
                  onChange={(e) => {
                    setHistoricalFilter(e.target.value);
                    setCurrentPage(1); // reset to page 1 on search
                  }}
                  placeholder="Search WO#, SKU, Title, Destination, Label..."
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                />
              </div>

              {/* Destination Select */}
             

               <div className="relative min-w-[200px]">
  <button
    type="button"
    className="w-full flex justify-between px-3 py-2.5 border rounded-md text-sm bg-white text-left"
    onClick={() => setIsOpen(!isOpen)}
  >
    {historicalWarehouseFilter || "All Destinations"}
     <ChevronDown
    size={18}
    className={`transition-transform duration-200 font-bold ${
      isOpen ? "rotate-180" : ""
    }`}
  />
  </button>

  {isOpen && (
    <div className="absolute mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg max-h-[220px] overflow-y-auto z-30">
      <div
        className={`px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm font-medium border-b border-gray-100 ${
          !historicalWarehouseFilter
            ? "bg-indigo-50 text-indigo-700 font-semibold"
            : "text-gray-700"
        }`}
        onClick={() => {
          handleHistoricalWarehouseFilterChange("");
          setCurrentPage(1);
          setIsOpen(false);
        }}
      >
        All Destinations
      </div>

      {uniqueDestinations.length === 0 ? (
        <div className="px-3 py-2 text-xs text-gray-400">
          No destinations available
        </div>
      ) : (
        uniqueDestinations.map((dest) => (
          <div
            key={dest}
            className={`px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm ${
              historicalWarehouseFilter === dest
                ? "bg-indigo-50 text-indigo-700 font-semibold"
                : "text-gray-700"
            }`}
            onClick={() => {
              handleHistoricalWarehouseFilterChange(dest);
              setCurrentPage(1);
              setIsOpen(false);
            }}
          >
            {dest}
          </div>
        ))
      )}
    </div>
  )}
</div>

              <div className="min-w-[180px]">
                <select
                  value={historicalSyncFilter}
                  onChange={(e) => {
                    setHistoricalSyncFilter(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-2 focus:ring-indigo-500 transition"
                  // disabled={historicalTransfers.length === 0}
                >
                  <option value="all">All Actions</option>
                  <option value="synced">Synced to Shopify</option>
                  <option value="not_synced">Archived (No Sync)</option>
                </select>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-end gap-3 sm:gap-4">
                {useDateFilter && (
                  <div className="flex items-end gap-2">
                    <div className="min-w-[140px]">
                      <SingleDatePicker
                        value={selectedDate}
                        onChange={handleDateChange}
                        className="w-full sm:w-44"
                      />
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleDayNavigation("prev")}
                        className="p-2 rounded hover:bg-gray-100 text-gray-600 transition-colors"
                        title="Previous day"
                      >
                        ← Prev
                      </button>
                      <button
                        onClick={() => handleDayNavigation("next")}
                        disabled={selectedDate >= today}
                        className="p-2 rounded hover:bg-gray-100 text-gray-600 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        title="Next day"
                      >
                        Next →
                      </button>
                    </div>
                  </div>
                )}

                {/* Clear Filters */}
                {(useDateFilter ||
                  historicalFilter.trim() ||
                  historicalWarehouseFilter ||
                  historicalSyncFilter !== "all") && (
                  <div className="self-end">
                    <button
                      onClick={() => {
                        setUseDateFilter(false);
                        setHistoricalFilter("");
                        setHistoricalWarehouseFilter(null);
                        setHistoricalSyncFilter("all");
                        setCurrentPage(1);
                      }}
                      className="px-4 py-2.5 text-sm font-medium text-white bg-[#cf350d] hover:bg-red-800 border border-[#cf350d] rounded-md transition-colors whitespace-nowrap"
                    >
                      Clear filters
                    </button>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setUseDateFilter((prev) => !prev);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2.5 text-sm font-medium rounded-md transition-colors border whitespace-nowrap flex items-center gap-2 ${
                    useDateFilter
                      ? "bg-[#6ca351] text-white border-[#6ca351] hover:bg-green-700"
                      : "bg-[#365daf] text-white border-[#365daf] hover:bg-blue-700 "
                  }`}
                >
                  {useDateFilter ? "✓ Date filter ON" : "Apply date filter"}
                </button>
              </div>
            </div>
          </div>

          {filteredHistoricalTransfers.length > 0 && (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-600">
              <div>
                Showing {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredHistoricalTransfers.length,
                )}{" "}
                of{" "}
                <span className="font-medium">
                  {filteredHistoricalTransfers.length}
                </span>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Previous
                </button>

                <span className="font-medium px-2">
                  Page {currentPage} of {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1.5 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}

          {isLoading ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <div
                  key={i}
                  className="h-14 bg-gray-100 rounded animate-pulse"
                />
              ))}
            </div>
          ) : filteredHistoricalTransfers.length === 0 ? (
            <div className="py-12 px-4 text-center text-gray-600 bg-gray-50/70 rounded-lg border border-dashed border-gray-300">
              {historicalTransfers.length === 0 ? (
                <p className="text-lg font-medium">
                  No historical transfer records found.
                </p>
              ) : (
                <>
                  <p className="text-lg font-medium">
                    {useDateFilter
                      ? `No transfers found for ${formatDisplayDate(selectedDate)}`
                      : "No records match your current filters"}
                  </p>
                  <p className="mt-2 text-sm">
                    Try adjusting or clearing the filters above.
                  </p>
                </>
              )}
            </div>
          ) : (
            <>
              {/* Desktop Table */}
              <div className="hidden lg:block overflow-x-auto rounded-lg border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {[
                        "WO Number",
                        "Project #",
                        "SKU",
                        "Title",
                        "Label",
                        "Allergen",
                        "Net Weight",
                        "Planned Qty",
                        "Actual Qty",
                        "Status",
                        "Destination",
                        "Sync Status",
                        "Date",
                      ].map((header) => (
                        <th
                          key={header}
                          className="px-4 py-3.5 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider"
                        >
                          {header}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {paginatedData.map((record, idx) => (
                      <tr
                        key={`${record.woNumber}-${idx}`}
                        className="hover:bg-indigo-50/40 transition-colors"
                      >
                        <td className="px-4 py-3 font-medium text-indigo-700 whitespace-nowrap">
                          {record.woNumber}
                        </td>
                        <td className="px-4 py-3 text-gray-700">
                          {record.projectNumber || "—"}
                        </td>
                        <td className="px-4 py-3 font-mono text-sm">
                          {record.sku}
                        </td>
                        <td className="px-4 py-3 max-w-md">
                          <div className="truncate" title={record.title}>
                            {record.title}
                          </div>
                        </td>
                        <td className="px-4 py-3">{record.labelCategory}</td>
                        <td className="px-4 py-3">{record.allergen}</td>
                        <td className="px-4 py-3">{record.netWeight}</td>
                        <td className="px-4 py-3 text-right">
                          {record.plannedQty}
                        </td>
                        <td className="px-4 py-3 text-right font-medium">
                          {record.actualQty}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              record.status === "Completed"
                                ? "bg-green-100 text-green-800"
                                : record.status === "Approved"
                                  ? "bg-blue-100 text-blue-800"
                                  : "bg-amber-100 text-amber-800"
                            }`}
                          >
                            {record.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 max-w-xs">
                          <div
                            className="whitespace-pre-line text-sm"
                            title={record.destination}
                          >
                            {record.destination}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              record.syncStatus === "synced"
                                ? "bg-emerald-100 text-emerald-800"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {record.syncStatus === "synced"
                              ? "Synced"
                              : "Archived (No Sync)"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                          {record.date}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile / Tablet Card View */}
              <div className="lg:hidden space-y-4">
                {filteredHistoricalTransfers.map((record) => (
                  <div
                    key={record.woNumber}
                    className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-base font-bold text-indigo-700">
                          {record.woNumber}
                        </div>
                        <div className="text-sm text-gray-500">
                          {record.sku}
                        </div>
                      </div>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium ${
                          record.status === "Completed"
                            ? "bg-green-100 text-green-800"
                            : record.status === "Approved"
                              ? "bg-blue-100 text-blue-800"
                              : "bg-amber-100 text-amber-800"
                        }`}
                      >
                        {record.status}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                      <div className="text-gray-500">Project #</div>
                      <div>{record.projectNumber || "—"}</div>

                      <div className="text-gray-500">Title</div>
                      <div
                        className="col-span-2 font-medium truncate"
                        title={record.title}
                      >
                        {record.title}
                      </div>

                      <div className="text-gray-500">Label</div>
                      <div>{record.labelCategory}</div>

                      <div className="text-gray-500">Allergen</div>
                      <div>{record.allergen}</div>

                      <div className="text-gray-500">Net Weight</div>
                      <div>{record.netWeight}</div>

                      <div className="text-gray-500">
                        Planned / Actual
                      </div>
                      <div className="font-medium">
                        {record.plannedQty} / {record.actualQty}
                      </div>

                      <div className="text-gray-500">Destination(s)</div>
                      <div className="col-span-2 whitespace-pre-line text-gray-700">
                        {record.destination}
                      </div>

                      <div className="text-gray-500">Sync Status</div>
                      <div className="font-medium">
                        {record.syncStatus === "synced"
                          ? "Synced"
                          : "Archived (No Sync)"}
                      </div>

                      <div className="text-gray-500">Date</div>
                      <div className="font-medium">{record.date}</div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Bottom Pagination */}
          {filteredHistoricalTransfers.length > itemsPerPage && (
            <div className="flex justify-center sm:justify-between items-center gap-4 pt-4 border-t border-gray-200 text-sm text-gray-600">
              <div className="hidden sm:block">
                Showing {(currentPage - 1) * itemsPerPage + 1}–
                {Math.min(
                  currentPage * itemsPerPage,
                  filteredHistoricalTransfers.length,
                )}{" "}
                of {filteredHistoricalTransfers.length}
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(prev - 1, 1))
                  }
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Previous
                </button>

                <span className="px-3 py-2 font-medium">
                  {currentPage} / {totalPages}
                </span>

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(prev + 1, totalPages))
                  }
                  disabled={currentPage === totalPages}
                  className="px-4 py-2 border border-gray-300 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 transition"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={async () => {
          setShowConfirmModal(false);
          await confirmTransfer();
        }}
        title="Confirm Transfer & Shopify Sync"
        confirmText="Sync & Move to History"
        isLoading={isLoading}
        confirmButtonClass="bg-black hover:bg-gray-800 text-white transition-colors"
      >
        <div className="space-y-4">
          {/* Description */}
          <p className="text-sm text-gray-700">
            You're about to process {selectedWorkOrders.size} selected work
            order{selectedWorkOrders.size !== 1 ? "s" : ""}. This will:
          </p>

          {/* Action List */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <ul className="text-sm text-gray-800 list-disc pl-5 space-y-2">
              <li>
                Sync transfer quantities from <strong>The Store</strong> to
                <strong> Shopify Warehouse 1580</strong>
              </li>
              <li>
                Save transfer records and move them to the
                <strong> Historical</strong> tab
              </li>
            </ul>
          </div>

          {/* Warning Section */}
          <div className="bg-amber-50 border border-amber-300 rounded-xl p-4">
            <h4 className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-2">
              Important
            </h4>
            <p className="text-sm text-amber-800">
              Ensure all required headers are copied to ERPAG before proceeding.
            </p>
            <p className="text-sm text-amber-800 mt-2">
              If the Shopify sync fails, the historical record will{" "}
              <strong>not</strong> be created.
            </p>
          </div>
        </div>
      </ConfirmModal>
    </div>
  );
};

export default Transfers;

